import React, { useState, useEffect } from 'react';
import { Search, Calculator, TrendingUp, AlertTriangle, CheckCircle, Info, Star, Home, Building, MapPin } from 'lucide-react';
import { masterSupabase as supabase } from '../../../lib/supabase-master.js';

const BaurechtTab = () => {
  // State Management
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [baurechtData, setBaurechtData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lernmodus, setLernmodus] = useState('einsteiger');
  const [analysisResults, setAnalysisResults] = useState(null);
  
  // Eingabefelder - GETRENNT für bessere UX
  const [strasse, setStrasse] = useState('');
  const [hausnummer, setHausnummer] = useState('');
  const [plz, setPlz] = useState('');
  const [stadt, setStadt] = useState('');
  
  // Autocomplete State
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  // Supabase-Daten laden
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('address');
      
      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Properties:', err);
      setError('Fehler beim Laden der Immobilien-Daten');
    }
  };

  const loadBaurechtData = async (address) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('baurecht_data')
        .select('*')
        .ilike('address', `%${address}%`)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      setBaurechtData(data);
    } catch (err) {
      console.error('Fehler beim Laden der Baurecht-Daten:', err);
      setBaurechtData(null);
    } finally {
      setLoading(false);
    }
  };

  // VERBESSERTE AUTOCOMPLETE-LOGIK - SANFT UND BENUTZERFREUNDLICH
  const handleStrasseChange = (value) => {
    setStrasse(value);
    
    if (value.length >= 2) {
      // Fuzzy-Matching für Tippfehler-Toleranz
      const filteredSuggestions = properties.filter(property => {
        const address = property.address?.toLowerCase() || '';
        const searchTerm = value.toLowerCase();
        
        // Levenshtein-Distanz für Tippfehler-Toleranz
        return address.includes(searchTerm) || 
               levenshteinDistance(address, searchTerm) <= 3;
      }).slice(0, 5); // Maximal 5 Vorschläge
      
      setSuggestions(filteredSuggestions);
      setShowSuggestions(filteredSuggestions.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Levenshtein-Distanz für Tippfehler-Toleranz
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  // KORREKTE FELD-AUFTEILUNG bei Suggestion-Auswahl
  const handleSuggestionClick = (property) => {
    // Parse die Adresse korrekt in die einzelnen Felder
    const addressParts = parseAddress(property.address);
    
    setStrasse(addressParts.street);
    setHausnummer(addressParts.number);
    setPlz(addressParts.postal_code || property.postal_code || '');
    setStadt(addressParts.city || property.city || '');
    
    setSelectedProperty(property);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Lade Baurecht-Daten für diese Property
    loadBaurechtData(property.address);
  };

  // Intelligente Adress-Parsing-Funktion
  const parseAddress = (fullAddress) => {
    if (!fullAddress) return { street: '', number: '', postal_code: '', city: '' };
    
    // Regex für deutsche Adressen: "Straßenname Hausnummer, PLZ Stadt"
    const addressRegex = /^(.+?)[\s,]+(\d+[a-zA-Z]?)(?:[\s,]+(\d{5}))?(?:[\s,]+(.+))?$/;
    const match = fullAddress.match(addressRegex);
    
    if (match) {
      return {
        street: match[1]?.trim() || '',
        number: match[2]?.trim() || '',
        postal_code: match[3]?.trim() || '',
        city: match[4]?.trim() || ''
      };
    }
    
    // Fallback: Einfache Aufteilung
    const parts = fullAddress.split(/[\s,]+/);
    return {
      street: parts[0] || '',
      number: parts[1] || '',
      postal_code: parts[2] || '',
      city: parts.slice(3).join(' ') || ''
    };
  };

  // Keyboard-Navigation für Suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestion >= 0) {
        handleSuggestionClick(suggestions[activeSuggestion]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };

  const handleAnalyze = async () => {
    if (!strasse || !hausnummer) {
      setError('Bitte geben Sie mindestens Straße und Hausnummer ein');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullAddress = `${strasse} ${hausnummer}`;
      await loadBaurechtData(fullAddress);
      
      // Berechne Potenzial-Analyse
      const results = calculateBaurechtPotential(selectedProperty, baurechtData);
      setAnalysisResults(results);
    } catch (err) {
      setError('Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const calculateBaurechtPotential = (property, baurecht) => {
    // Demo-Berechnung für Ilvesheim (falls keine echten Baurecht-Daten)
    const defaultGRZ = 0.4;
    const defaultGFZ = 0.8;
    const defaultMaxFloors = 2;
    const defaultMaxHeight = 9.5;

    const grz = baurecht?.grz || defaultGRZ;
    const gfz = baurecht?.gfz || defaultGFZ;
    const maxFloors = baurecht?.max_floors || defaultMaxFloors;
    const maxHeight = baurecht?.max_height_m || defaultMaxHeight;

    // Grundstücksgröße aus Property-Daten
    const lotSize = property?.lot_size || 475; // Default für Scheffelstraße 10
    const currentArea = property?.total_area || 233; // Aktuelle Wohnfläche

    // Berechnungen
    const maxGrundflaeche = lotSize * grz;
    const maxGeschossflaeche = lotSize * gfz;
    const currentGrzAuslastung = (currentArea / maxGrundflaeche) * 100;
    const currentGfzAuslastung = (currentArea / maxGeschossflaeche) * 100;
    const verbleibendesPotential = Math.max(0, maxGeschossflaeche - currentArea);

    // Potenzial-Rating (1-5 Sterne)
    let rating = 1;
    if (currentGfzAuslastung < 50) rating = 5;
    else if (currentGfzAuslastung < 60) rating = 4;
    else if (currentGfzAuslastung < 75) rating = 3;
    else if (currentGfzAuslastung < 90) rating = 2;

    return {
      grz,
      gfz,
      maxFloors,
      maxHeight,
      lotSize,
      currentArea,
      maxGrundflaeche,
      maxGeschossflaeche,
      currentGrzAuslastung,
      currentGfzAuslastung,
      verbleibendesPotential,
      rating,
      scenarios: {
        konservativ: Math.floor(verbleibendesPotential * 0.5),
        standard: Math.floor(verbleibendesPotential * 0.75),
        maximal: Math.floor(verbleibendesPotential)
      }
    };
  };

  return (
    <div className="space-y-6">
      {/* Header mit poetischer Einleitung */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Building className="h-8 w-8" />
          <h2 className="text-2xl font-bold">Intelligente Baurecht-Analyse</h2>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">KI-optimiert</span>
          <span className="bg-green-500/80 px-3 py-1 rounded-full text-sm font-medium">Lernfähig</span>
        </div>
        
        <p className="text-purple-100 mb-4 leading-relaxed">
          Die Kunst des Verstehens in einer KI-Welt: Während Algorithmen Zahlen berechnen können, verstehen nur Sie die 
          Nuancen von Baurecht und Potenzial. Lernen Sie nicht trotz der KI, sondern mit ihr – als Partner für intelligente 
          Investment-Entscheidungen.
        </p>

        {/* Lernmodus-Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setLernmodus('einsteiger')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              lernmodus === 'einsteiger' 
                ? 'bg-white text-purple-600' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Einsteiger
          </button>
          <button
            onClick={() => setLernmodus('experte')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              lernmodus === 'experte' 
                ? 'bg-white text-purple-600' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Experte
          </button>
        </div>

        {lernmodus === 'einsteiger' && (
          <div className="mt-3 flex items-center gap-2 text-yellow-200">
            <Info className="h-4 w-4" />
            <span className="text-sm">Bewegen Sie die Maus über unterstrichene Begriffe für Erklärungen</span>
          </div>
        )}
      </div>

      {/* Immobilien-Daten Eingabe - CLEAN SOLUTION WITHOUT !IMPORTANT */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg relative mb-40 pb-32 min-h-[500px]">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Immobilien-Daten</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Straße mit Autocomplete - FIXED POSITIONING */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Straße
            </label>
            <input
              type="text"
              value={strasse}
              onChange={(e) => handleStrasseChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => strasse.length >= 2 && setShowSuggestions(suggestions.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="z.B. Scheffelstraße"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            
            {/* FIXED: Suggestions Dropdown - ABSOLUTE POSITIONING WITH HIGH Z-INDEX */}
            {showSuggestions && suggestions.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                style={{ 
                  zIndex: 9999,
                  position: 'absolute'
                }}
              >
                {suggestions.map((property, index) => (
                  <div
                    key={property.id}
                    onClick={() => handleSuggestionClick(property)}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                      index === activeSuggestion 
                        ? 'bg-blue-50 dark:bg-blue-900/20' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {property.address}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {property.property_type} • {property.year_built} • {property.total_area}m²
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hausnummer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hausnummer
            </label>
            <input
              type="text"
              value={hausnummer}
              onChange={(e) => setHausnummer(e.target.value)}
              placeholder="z.B. 10"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* PLZ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              PLZ
            </label>
            <input
              type="text"
              value={plz}
              onChange={(e) => setPlz(e.target.value)}
              placeholder="z.B. 68549"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Stadt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Stadt
            </label>
            <input
              type="text"
              value={stadt}
              onChange={(e) => setStadt(e.target.value)}
              placeholder="z.B. Ilvesheim"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Info-Box */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">Intelligente Baurecht-Analyse</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Das System analysiert automatisch die Bebauungsmöglichkeiten basierend auf{' '}
                {lernmodus === 'einsteiger' ? (
                  <>
                    <span className="underline decoration-dotted cursor-help" title="Grundflächenzahl - Anteil des Grundstücks, der bebaut werden darf">GRZ</span>,{' '}
                    <span className="underline decoration-dotted cursor-help" title="Geschossflächenzahl - Verhältnis der Geschossfläche zur Grundstücksfläche">GFZ</span>
                  </>
                ) : (
                  'GRZ, GFZ'
                )}{' '}
                und weiteren Baurecht-Parametern.
              </p>
            </div>
          </div>
        </div>

        {/* Analyse-Button */}
        <div className="mt-6">
          <button
            onClick={handleAnalyze}
            disabled={loading || !strasse || !hausnummer}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Analysiere...
              </>
            ) : (
              <>
                <Calculator className="h-5 w-5" />
                Baurecht analysieren
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Analyse-Ergebnisse */}
      {analysisResults && (
        <div className="space-y-6">
          {/* Entwicklungspotenzial */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Entwicklungspotenzial</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${
                        i < analysisResults.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Potenzial-Rating ({analysisResults.rating}/5)</p>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {Math.round(analysisResults.verbleibendesPotential)}m²
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Verbleibendes Potenzial</p>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {analysisResults.currentGfzAuslastung.toFixed(1)}%
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">GFZ-Auslastung</p>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  GRZ: {analysisResults.grz}
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  GFZ: {analysisResults.gfz}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Max. {analysisResults.maxFloors} Geschosse
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Max. {analysisResults.maxHeight}m Höhe
                </div>
              </div>
            </div>

            {/* Erweiterungsszenarien */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Erweiterungsszenarien</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 dark:text-white">Konservativ</h5>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400 my-2">
                    +{analysisResults.scenarios.konservativ}m²
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Sicher genehmigungsfähige Erweiterung
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded">
                    Niedrig Risiko
                  </span>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 dark:text-white">Standard</h5>
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400 my-2">
                    +{analysisResults.scenarios.standard}m²
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Optimale Ausnutzung mit moderatem Risiko
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded">
                    Mittel Risiko
                  </span>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 dark:text-white">Maximal</h5>
                  <div className="text-xl font-bold text-orange-600 dark:text-orange-400 my-2">
                    +{analysisResults.scenarios.maximal}m²
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Vollausnutzung, evtl. Ausnahmeverfahren nötig
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs rounded">
                    Hoch Risiko
                  </span>
                </div>
              </div>
            </div>

            {/* Datenquelle */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <strong>Datenquelle:</strong> {baurechtData ? 'Offizielle Baurecht-Daten' : 'Demo-Daten (Ilvesheim Wohngebiet)'}
                </div>
                <div>
                  <strong>Vertrauenswert:</strong> {baurechtData ? '90%' : '75%'}
                </div>
              </div>
              {!baurechtData && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  <strong>Hinweis:</strong> Typische Werte für Ilvesheimer Wohngebiete. Verifikation durch Bebauungsplan erforderlich.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaurechtTab;

